import { Button, Empty, Input, LoadingSpinner, mapDataToSuggestions, SmartDropdown, SmartDropdownOption, SuggestionMapParams } from "@xapp/components";
import { Template, TemplateType } from "@xapp/ovai-lib";
import { RadioIcon, RadioIconGroup } from "components/StentorUIComponents";
import { Organization, SearchedOrg } from "generated/graphql";
import { ClientApp } from "models";
import React, { useState, useCallback, useEffect } from "react";
import forms from "styles/forms.scss";
import { Noop } from "utils/Noop";
import { idify } from "utils/String";
import { CUSTOM_TYPE, ON_DEMAND_TEMPLATE, RADIO_TEMPLATE } from "constants/templates";

export interface Field {
    disabled?: boolean;
    value?: string;
    show?: boolean;
}

export interface TemplateField extends Field {
    value?: TemplateType | undefined;
}

export interface FormFields {
    appId?: Field;
    organizationId?: Field;
    templateType?: TemplateField;
}

export interface NewAppFormProps {
    defaultFieldValues?: FormFields;
    availableTemplates: (TemplateType | string)[];
    isBusy?: boolean;
    organizations: Pick<Organization, "organizationId" | "name">[];
    onSave(app: ClientApp | Template): void;
    onCancel(): void;
}

const defaultProps: NewAppFormProps = {
    availableTemplates: ["CUSTOM"],
    defaultFieldValues: {
        appId: {
            disabled: false,
            show: false,
            value: undefined
        },
        organizationId: {
            disabled: false,
            show: false,
            value: undefined
        },
        templateType: {
            disabled: false,
            show: true,
            value: undefined
        }
    },
    onCancel: Noop,
    onSave: Noop,
    organizations: []
};

export const NewAppForm: React.FC<NewAppFormProps> = (props) => {
    const {
        availableTemplates = defaultProps.availableTemplates,
        defaultFieldValues = defaultProps.defaultFieldValues,
        isBusy: propsBusy,
        organizations = defaultProps.organizations,
        onCancel = defaultProps.onCancel,
        onSave = defaultProps.onSave
    } = props;
    
    const { appId, organizationId, templateType } = defaultFieldValues;

    const [app, setApp] = useState<ClientApp>({
        appId: appId ? appId.value || "" : "",
        name: "",
        organizationId: organizationId ? organizationId.value || "" : "",
        templateType: templateType ? templateType.value : undefined
    } as ClientApp);
    
    const [formIsValid, setFormIsValid] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [slugLabel, setSlugLabel] = useState("");

    useEffect(() => {
        setIsBusy(propsBusy || false);
    }, [propsBusy]);

    const getOrgNameFromID = useCallback((orgId: string): string => {
        const selectedOrg = organizations.find((org: SearchedOrg) => org.organizationId === orgId);
        return selectedOrg?.name || "";
    }, [organizations]);

    const handleChange = useCallback((key: string, value: string) => {
        let newApp = { ...app, [key]: value };

        // idify the appId as a suggestion to the user
        if (key === "name") {
            const appId = idify(value);
            newApp = { ...newApp, appId };
        }

        setApp(newApp);
    }, [app]);

    const handleOrgChange = useCallback((suggestion: SmartDropdownOption) => {
        setApp(prevApp => ({
            ...prevApp,
            organizationId: suggestion.id
        }));
        setSlugLabel(getOrgNameFromID(suggestion.id));
    }, [getOrgNameFromID]);

    const handleSave = useCallback(() => {
        onSave(app);
    }, [onSave, app]);

    const validateForm = useCallback(() => {
        const { name, templateType: appTemplateType } = app;
        let valid = false;

        if (name !== "" && (defaultFieldValues.templateType?.show ? appTemplateType !== undefined : true)) {
            valid = true;
        }

        setFormIsValid(valid);
    }, [app, defaultFieldValues.templateType]);
    
    useEffect(() => {
        validateForm();
    }, [validateForm]);

    const renderTypeFields = useCallback((show: boolean) => {
        const { templateType } = app;

        if (show) {

            const icons: JSX.Element[] = [];

            availableTemplates.forEach((type, index) => {
                switch (type) {
                    case RADIO_TEMPLATE:
                        icons.push(
                            <RadioIcon
                                key={`${type}-${index}`}
                                name="templateType"
                                label="Radio"
                                value={RADIO_TEMPLATE}
                                icon="radio"
                            />
                        );
                        break;
                    case ON_DEMAND_TEMPLATE:
                        icons.push(
                            <RadioIcon
                                key={`${type}-${index}`}
                                name="templateType"
                                label="Podcast"
                                value={ON_DEMAND_TEMPLATE}
                                icon="rss_feed"
                            />
                        );
                        break;
                    case "OC_STUDIO_STARTER_TEMPLATE":
                        icons.push(
                            <RadioIcon
                                key={`${type}-${index}`}
                                name="templateType"
                                label="CSS4CCI"
                                value={"OC_STUDIO_STARTER_TEMPLATE"}
                                icon="help_outline"
                            />
                        );
                        break;
                    case "CUSTOM":
                    default:
                        icons.push(
                            <RadioIcon
                                key={`${type}-${index}`}
                                name="templateType"
                                label="Custom"
                                value={CUSTOM_TYPE}
                                icon="build"
                            />
                        );
                }
            });

            return (
                <div className={forms.fieldset}>
                    <h3>What type of app?</h3>

                    <RadioIconGroup
                        ariaLabel="Select App Type"
                        onChange={handleChange}
                        selectedOption={templateType}
                    >
                        {icons}
                    </RadioIconGroup>
                    {availableTemplates.includes("CUSTOM") ? <p>Select Custom to start from scratch</p> : <></>}
                </div>
            );
        }
    }, [availableTemplates, app, handleChange]);

    const renderBusyState = useCallback(() => {
        return (
            <Empty title="Hold Tight. We are creating your new App">
                <LoadingSpinner />
            </Empty>
        );
    }, []);
    
    const renderOrgSelector = useCallback(() => {
        const suggestionMapParams: SuggestionMapParams = {
            dataSet: organizations,
            idField: "organizationId",
            nameField: "name"
        };
        const dropdownOptions = mapDataToSuggestions(suggestionMapParams);

        return (
            <div className={forms.fieldset}>
                <h3>Which Organization should this App be added to?</h3>

                <SmartDropdown
                    options={dropdownOptions}
                    onChange={handleOrgChange}
                    slugLabel={slugLabel}
                />
            </div>
        );
    }, [organizations, handleOrgChange, slugLabel]);
    
    if (isBusy) {
        return renderBusyState();
    }

    return (
        <section>
            <div>
                {organizationId.show && renderOrgSelector()}

                <div className={forms.fieldset}>
                    <h3>What is the name of your app?</h3>

                    <Input
                        data-puppet="create-app-input_name"
                        type="text"
                        label="Name"
                        value={app.name}
                        onChange={(value) => handleChange("name", String(value))}
                        variation="standard"
                    />
                </div>

                {appId.show && (
                    <Input
                        type="text"
                        label="App ID"
                        value={app.appId}
                        disabled={appId.disabled}
                        onChange={(value) => handleChange("appId", String(value))}
                        variation="standard"
                    />
                )}

                {renderTypeFields(templateType.show)}
            </div>

            <footer className={forms.footer}>
                <Button variation="flat" label="Cancel" onClick={onCancel} />
                <Button variation="primary" label="Create App" disabled={!formIsValid} onClick={handleSave} />
            </footer>
        </section>
    );
};